<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize, useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import AbratelPanelModal from 'dashboard/components/abratel/AbratelPanelModal.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// Lightbox da foto do contato (clicar no avatar do header amplia a imagem)
const avatarLightboxOpen = ref(false);
const openAvatarLightbox = () => {
  if (currentContact.value && currentContact.value.thumbnail) {
    avatarLightboxOpen.value = true;
  }
};
const closeAvatarLightbox = () => {
  avatarLightboxOpen.value = false;
};

// Botão "Abrir Ticket" — antes injetado pelo inject.js no header (DOM-scraping
// de .header-actions-wrap); agora nativo. Abre o ticket.html do painel num
// modal com a conversa e conta atuais.
const ticketModalOpen = ref(false);
// Token: o getter do Vuex nem sempre expõe access_token; o inject.js captura
// dos headers XHR/fetch em window._cwAuthToken (mesma ponte dos embeds).
const panelToken = () =>
  store.getters['auth/getCurrentUser']?.access_token ||
  window._cwAuthToken ||
  '';
const ticketUrl = computed(() => {
  return `/_panel/ticket.html?token=${encodeURIComponent(panelToken())}&conversation_id=${currentChat.value.id}&account_id=${accountId.value}`;
});
const openTicketModal = () => {
  if (!panelToken()) {
    useAlert('Sessão não encontrada. Faça login novamente.');
    return;
  }
  ticketModalOpen.value = true;
};

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

// ── Abratel customization: clickable inbox name → transfer dropdown ──────
const [showInboxMenu, toggleInboxMenu] = useToggle(false);

const apiInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(
    i => i.channel_type === 'Channel::Api'
  )
);

const inboxMenuItems = computed(() =>
  apiInboxes.value
    .filter(i => i.id !== inbox.value?.id)
    .map(i => ({
      label: i.name,
      value: i.id,
      action: 'transfer',
    }))
);

const canTransfer = computed(() => inboxMenuItems.value.length > 0);

const handleInboxMenuAction = async ({ value }) => {
  toggleInboxMenu(false);
  try {
    const accessToken = store.getters.getCurrentUser?.access_token;
    const { data } = await ConversationApi.transferInbox(
      accountId.value,
      props.chat.id,
      value,
      accessToken
    );
    useAlert(`Conversa transferida para ${data.target_inbox || ''}`);
    setTimeout(() => window.location.reload(), 600);
  } catch (error) {
    useAlert(error.response?.data?.error || 'Falha na transferência');
  }
};

// ── Abratel customization: shared Nota (radar_summary custom attribute) ──
const radarNote = ref(props.chat?.custom_attributes?.radar_summary || '');
const isEditingNote = ref(false);
watch(
  () => props.chat?.custom_attributes?.radar_summary,
  v => {
    if (!isEditingNote.value) radarNote.value = v || '';
  }
);
const saveNote = async () => {
  const original = props.chat?.custom_attributes?.radar_summary || '';
  const next = (radarNote.value || '').trim();
  isEditingNote.value = false;
  if (next === original) return;
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: props.chat.id,
      customAttributes: { radar_summary: next },
    });
    useAlert('Nota salva');
  } catch (e) {
    useAlert('Erro ao salvar nota: ' + (e.message || 'falha'));
    radarNote.value = original;
  }
};
const cancelNoteEdit = () => {
  radarNote.value = props.chat?.custom_attributes?.radar_summary || '';
  isEditingNote.value = false;
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <button
        type="button"
        class="flex-shrink-0 p-0 m-0 bg-transparent border-0 rounded-full"
        :class="currentContact.thumbnail ? 'cursor-pointer' : 'cursor-default'"
        :title="currentContact.thumbnail ? 'Ver foto' : ''"
        @click="openAvatarLightbox"
      >
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
        />
      </button>
      <div class="flex flex-col items-start min-w-0 ms-2">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 min-w-0 text-xs conversation--header--actions text-n-slate-11"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <div
            v-if="hasMultipleInboxes"
            v-on-click-outside="() => toggleInboxMenu(false)"
            class="relative"
          >
            <button
              v-if="canTransfer"
              type="button"
              class="flex items-center gap-1 rounded px-1 -mx-1 hover:bg-n-alpha-1 cursor-pointer"
              title="Transferir caixa de entrada"
              @click="toggleInboxMenu()"
            >
              <InboxName :inbox="inbox" class="!mx-0" />
              <fluent-icon icon="chevron-down" size="10" />
            </button>
            <InboxName v-else :inbox="inbox" class="!mx-0" />
            <DropdownMenu
              v-if="showInboxMenu && canTransfer"
              :menu-items="inboxMenuItems"
              class="absolute top-full left-0 z-50 mt-1 min-w-[200px]"
              @action="handleInboxMenuAction"
            />
          </div>
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <!-- Abratel: Nota (radar_summary) inline edit — centered horizontally between left/right -->
    <div class="flex-1 flex items-center justify-center min-w-0 px-2 hidden xl:flex">
      <div class="flex items-center gap-1 min-w-0 max-w-md">
        <span class="text-base text-n-slate-11 flex-shrink-0">📝</span>
        <input
          v-if="isEditingNote"
          v-model="radarNote"
          type="text"
          placeholder="Nota da conversa"
          class="bg-n-alpha-2 text-sm rounded px-2 py-1 outline-none focus:ring-1 focus:ring-n-brand min-w-0 w-72 text-n-slate-12"
          maxlength="500"
          @blur="saveNote"
          @keydown.enter.prevent="saveNote"
          @keydown.esc.prevent="cancelNoteEdit"
        />
        <button
          v-else
          type="button"
          class="text-sm text-n-slate-11 hover:text-n-slate-12 truncate text-center min-w-0 px-2 py-0.5 rounded hover:bg-n-alpha-1"
          :title="radarNote || 'Adicionar nota — sincroniza com o Radar'"
          @click="isEditingNote = true"
        >
          <span v-if="radarNote" class="truncate">{{ radarNote }}</span>
          <span v-else class="italic text-n-slate-10">Adicionar nota</span>
        </button>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <button
        type="button"
        class="inline-flex items-center gap-1 px-3 py-1 border-[1.5px] border-[#e53935] rounded-md bg-white dark:bg-transparent text-[#e53935] text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-[#e53935]/10"
        title="Abrir Ticket"
        @click="openTicketModal"
      >
        🎫 Abrir Ticket
      </button>
      <MoreActions :conversation-id="currentChat.id" />
    </div>

    <AbratelPanelModal
      v-if="ticketModalOpen"
      title="🎫 Abrir Ticket"
      :url="ticketUrl"
      @close="ticketModalOpen = false"
    />

    <!-- Lightbox: foto do contato ampliada ao clicar no avatar do header -->
    <teleport to="body">
      <div
        v-if="avatarLightboxOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 cursor-zoom-out"
        @click="closeAvatarLightbox"
      >
        <button
          type="button"
          class="absolute top-4 right-4 text-white/90 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center text-2xl leading-none"
          @click.stop="closeAvatarLightbox"
        >
          ×
        </button>
        <img
          :src="currentContact.thumbnail"
          :alt="currentContact.name"
          class="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl object-contain"
          @click.stop
        />
      </div>
    </teleport>
  </div>
</template>
